/**
 * Builds the self-contained skill block a user copies from the Connections screen and pastes into
 * their own Claude Code. It is instructions + the minted token baked in, so the paste needs no install
 * and no further auth — just `curl`, which every terminal already has.
 *
 * The copy is deliberately NOT localized. It is pasted into a coding agent as an instruction, not read
 * by the user as UI; keeping it one canonical English block means every agent gets the same contract
 * and the token/endpoint can't drift per locale. The surrounding UI chrome IS localized.
 */

// M1 ships on the existing public path; the clean `/agent/*` mapping lands later (plan M4). Kept as
// constants so the M4 rename is one edit here, not a hunt through a template string.
const API_BASE = 'https://symphony.wix.com/individuals-chat/poc';
export const AGENT_ASK_URL = `${API_BASE}/agent/ask`;
export const AGENT_REPLY_URL = `${API_BASE}/agent/reply`;

export function buildClaudeSkill(token: string): string {
  return `# Talk to my Symphony

You can act on my **Symphony** — my team of AI business agents. Symphony can schedule, reach out to my
contacts, follow up, build reports, and act on my business, as me. A message you send here is treated as
coming from me — it carries my full authority to act.

## 1. Send a message

\`\`\`bash
curl -s ${AGENT_ASK_URL} \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "<what you want to ask or tell Symphony>", "sessionId": "claude-code"}'
\`\`\`

The response is JSON. If it has \`.reply\`, that is Symphony's answer — done. If instead \`.status\` is
\`"accepted"\` and there is no \`.reply\`, Symphony is still working (its turns can take a minute or two);
keep the \`.conversationId\` it returned and poll for the answer:

## 2. Poll for the reply (only when step 1 returned \`"accepted"\`)

\`\`\`bash
curl -s ${AGENT_REPLY_URL} \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"conversationId": "<the conversationId from step 1>"}'
\`\`\`

Repeat this every ~3 seconds until \`.status\` is \`"answered"\` and \`.reply\` is present, then show me the
\`.reply\`. Give up after ~2 minutes. Keep the same \`sessionId\` on every \`ask\` so replies stay in one thread.

This token authorizes this machine to act as me. Keep it secret — anyone who has it can act as me. I can
revoke it anytime in Symphony → Settings → Connections.`;
}
